import moment from 'moment-timezone';

import { BusyInterval } from 'src/services/calendar/calendar.types';

// ----------------------------------------------------------------------

export const MEETING_DURATION_OPTIONS = [15, 30, 45, 60, 90, 120];

export const MEETING_SLOT_STEP_MINUTES = 30;

export type MeetingSlotPeriod = 'overnight' | 'morning' | 'afternoon' | 'evening';

export const MEETING_SLOT_PERIODS: MeetingSlotPeriod[] = ['overnight', 'morning', 'afternoon', 'evening'];

export interface MeetingSlot {
  /** ISO UTC, unique per slot even on the repeated hour of a DST fall-back day. */
  key: string;
  start: moment.Moment;
  end: moment.Moment;
  period: MeetingSlotPeriod;
  disabled: boolean;
}

export const getMeetingSlotPeriod = (hour: number): MeetingSlotPeriod => {
  if (hour < 6) return 'overnight';
  if (hour < 12) return 'morning';
  if (hour < 17) return 'afternoon';
  return 'evening';
};

/** Half-open overlap: a meeting ending exactly when a busy block starts is free. */
export const overlapsBusyInterval = (
  start: moment.Moment,
  end: moment.Moment,
  busyIntervals: BusyInterval[]
) => busyIntervals.some((interval) => start.isBefore(moment(interval.end)) && end.isAfter(moment(interval.start)));

interface BuildMeetingSlotsOptions {
  date: string; // YYYY-MM-DD
  timezone: string;
  durationMinutes: number;
  busyIntervals?: BusyInterval[];
  now?: moment.Moment;
}

/**
 * Start times across the whole local day in `timezone`. Steps in absolute time, so a DST change day
 * yields 46 or 50 slots rather than skipping or duplicating a wall-clock hour.
 */
export const buildMeetingSlots = ({
  date,
  timezone,
  durationMinutes,
  busyIntervals = [],
  now = moment(),
}: BuildMeetingSlotsOptions): MeetingSlot[] => {
  const dayStart = moment.tz(date, 'YYYY-MM-DD', timezone).startOf('day');
  const dayEnd = dayStart.clone().add(1, 'day');

  const slots: MeetingSlot[] = [];

  for (let cursor = dayStart.clone(); cursor.isBefore(dayEnd); cursor.add(MEETING_SLOT_STEP_MINUTES, 'minutes')) {
    const start = cursor.clone();
    const end = start.clone().add(durationMinutes, 'minutes');

    slots.push({
      key: start.toISOString(),
      start,
      end,
      period: getMeetingSlotPeriod(start.hour()),
      disabled: !start.isAfter(now) || overlapsBusyInterval(start, end, busyIntervals),
    });
  }

  return slots;
};
